import React from 'react';
import { createPortal } from 'react-dom';
import { X, Printer, Download, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';

export const Invoice = ({ booking, onClose, invoiceNumber }) => {
  const handlePrint = () => {
    window.print();
  };

  // Normalize phone for wa.me link: digits only, default to India (91) when no country code
  const buildWhatsAppPhone = (raw) => {
    if (!raw) return '';
    const digits = String(raw).replace(/\D/g, '');
    if (!digits) return '';
    // If it already starts with a country code (length > 10), keep as is
    if (digits.length > 10) return digits;
    // 10-digit Indian numbers — prepend 91
    if (digits.length === 10) return `91${digits}`;
    return digits;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  // Calculate pricing based on service type
  const calculatePricing = () => {
    // Check if booking has custom pricing
    if (booking.custom_amount && booking.custom_breakdown) {
      return {
        isCustom: true,
        breakdown: booking.custom_breakdown,
        total: booking.custom_amount
      };
    }

    let baseAmount = 0;
    let details = '';

    switch (booking.serviceType?.toLowerCase()) {
      case 'airport':
        baseAmount = booking.vehicleType?.toLowerCase() === 'suv' ? 1850 : 1350;
        details = `${booking.vehicleType} Airport Transfer`;
        break;
      case 'outstation':
        baseAmount = 0; // Will be calculated based on distance
        details = 'Outstation Trip (Per km basis)';
        break;
      case 'rental':
        baseAmount = booking.vehicleType?.toLowerCase() === 'suv' ? 500 : 375;
        details = `${booking.vehicleType} Hourly Rental (Min 4 hrs)`;
        break;
      default:
        details = 'As per agreement';
    }

    return { baseAmount, details, isCustom: false };
  };

  const pricing = calculatePricing();

  // Compute the final amount due to display in the WhatsApp summary
  const getInvoiceTotal = () => {
    if (pricing.isCustom) {
      if (booking.serviceType?.toLowerCase() === 'airport') {
        return pricing.breakdown?.total_amount ?? pricing.total ?? 0;
      }
      return pricing.breakdown?.due_amount ?? pricing.total ?? 0;
    }
    return pricing.baseAmount || 0;
  };

  const handleSendWhatsApp = () => {
    const phone = buildWhatsAppPhone(booking.phone);
    if (!phone) {
      toast.error('Customer phone number is missing — cannot send via WhatsApp.');
      return;
    }

    const total = getInvoiceTotal();
    const totalLine = total > 0 ? `*Amount:* ₹${Number(total).toLocaleString('en-IN')}` : '*Amount:* As per agreement';
    const dateLine = booking.date ? `*Date:* ${booking.date}${booking.time ? ` at ${booking.time}` : ''}` : '';
    const pickup = booking.pickupLocation ? `*Pickup:* ${booking.pickupLocation}` : '';
    const drop = booking.dropLocation ? `*Drop:* ${booking.dropLocation}` : '';

    const message = [
      `Hello ${booking.name || 'there'},`,
      '',
      `Thank you for travelling with *SG Cabs*. Here are your trip & invoice details:`,
      '',
      `*Invoice:* #${invoiceNumber}`,
      `*Service:* ${booking.serviceType || '-'} (${booking.vehicleType || '-'})`,
      pickup,
      drop,
      dateLine,
      totalLine,
      '',
      `We hope you had a great ride! Share your feedback here: https://sg-cabs.com/feedback`,
      '',
      `🚖 SG Cabs | +91 75078 67648`,
    ].filter(Boolean).join('\n');

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success('Opening WhatsApp…');
  };

  return createPortal(
    <>
      {/* Overlay */}
      <div id="invoice-print-area-overlay" className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div id="invoice-print-area-wrapper" className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header with close button - hidden in print */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200 print:hidden">
            <h2 className="text-xl font-bold text-gray-900">Invoice</h2>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleSendWhatsApp}
                data-testid="invoice-send-whatsapp-btn"
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Send via WhatsApp
              </Button>
              <Button 
                onClick={handlePrint}
                data-testid="invoice-print-btn"
                className="bg-black hover:bg-gray-900 text-yellow-400"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button 
                onClick={onClose}
                data-testid="invoice-close-btn"
                variant="outline"
                className="border-gray-300"
              >
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
            </div>
          </div>

          {/* Invoice Content */}
          <div id="invoice-print-area" className="p-8 print:p-12 bg-white">
            {/* Simplified Header - Just SG Cabs and Invoice Number */}
            <div className="flex justify-between items-center mb-8 pb-6 border-b-2 border-gray-900">
              <div className="flex items-center space-x-4">
                <img 
                  src="/sg-cabs-logo.png" 
                  alt="SG Cabs Logo" 
                  className="w-20 h-20 object-contain"
                />
                <h1 className="text-4xl font-bold text-gray-900">SG Cabs</h1>
              </div>
              <div className="text-right">
                <h2 className="text-sm text-gray-600 mb-1">INVOICE</h2>
                <p className="text-3xl font-bold text-gray-900">#{invoiceNumber}</p>
                <p className="text-sm text-gray-600 mt-2">{formatDate(booking.created_at)}</p>
                {booking.createdBy ? (
                  <p className="text-xs text-gray-500 mt-1" data-testid="invoice-created-by">Created by: {booking.createdBy}</p>
                ) : null}
              </div>
            </div>

            {/* Customer Details */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Bill To:</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-900 font-semibold text-lg">{booking.name}</p>
                <p className="text-gray-600 mt-1">Phone: {booking.phone}</p>
                {booking.email && <p className="text-gray-600">Email: {booking.email}</p>}
              </div>
            </div>

            {/* Trip Details */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Trip Details:</h3>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-3 text-left font-semibold">Description</th>
                    <th className="border border-gray-300 p-3 text-left font-semibold">Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 p-3">Service Type</td>
                    <td className="border border-gray-300 p-3 font-medium">{booking.serviceType}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">Vehicle Type</td>
                    <td className="border border-gray-300 p-3 font-medium">{booking.vehicleType}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">Pickup Location</td>
                    <td className="border border-gray-300 p-3">{booking.pickupLocation || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">Drop Location</td>
                    <td className="border border-gray-300 p-3">{booking.dropLocation || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">Pickup Date</td>
                    <td className="border border-gray-300 p-3">{formatDate(booking.date)}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">Pickup Time</td>
                    <td className="border border-gray-300 p-3">{formatTime(booking.time)}</td>
                  </tr>
                  {booking.notes && (
                    <tr>
                      <td className="border border-gray-300 p-3">Additional Notes</td>
                      <td className="border border-gray-300 p-3">{booking.notes}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Car & Driver Details - if available */}
            {(booking.car_details || booking.driver_details) && (
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Assigned Vehicle & Driver:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {booking.car_details && (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                          <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/>
                        </svg>
                        Vehicle Details
                      </h4>
                      <p className="text-gray-700 font-medium">{booking.car_details.model}</p>
                      <p className="text-gray-600 text-sm mt-1">
                        <span className="font-semibold">Reg:</span> {booking.car_details.registration}
                      </p>
                    </div>
                  )}
                  {booking.driver_details && (
                    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-900 mb-2 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                        </svg>
                        Driver Details
                      </h4>
                      <p className="text-gray-700 font-medium">{booking.driver_details.name}</p>
                      <p className="text-gray-600 text-sm mt-1">
                        <span className="font-semibold">Phone:</span> {booking.driver_details.phone}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pricing */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Payment Details:</h3>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-3 text-left font-semibold">Item</th>
                    <th className="border border-gray-300 p-3 text-right font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {pricing.isCustom ? (
                    <>
                      {/* For Airport - show only Toll and Parking */}
                      {booking.serviceType?.toLowerCase() === 'airport' ? (
                        <>
                          <tr>
                            <td className="border border-gray-300 p-3">{booking.vehicleType} Airport Transfer</td>
                            <td className="border border-gray-300 p-3 text-right font-medium">₹{Number(pricing.breakdown.base_fare || (booking.vehicleType?.toLowerCase() === 'suv' ? 1850 : 1350)).toLocaleString('en-IN')}</td>
                          </tr>
                          {Number(pricing.breakdown.toll) > 0 ? (
                            <tr>
                              <td className="border border-gray-300 p-3">Toll Charges</td>
                              <td className="border border-gray-300 p-3 text-right font-medium">₹{Number(pricing.breakdown.toll).toLocaleString('en-IN')}</td>
                            </tr>
                          ) : null}
                          {Number(pricing.breakdown.parking) > 0 ? (
                            <tr>
                              <td className="border border-gray-300 p-3">Parking Charges</td>
                              <td className="border border-gray-300 p-3 text-right font-medium">₹{Number(pricing.breakdown.parking).toLocaleString('en-IN')}</td>
                            </tr>
                          ) : null}
                          {Number(pricing.breakdown.discount) > 0 ? (
                            <tr className="bg-amber-50">
                              <td className="border border-gray-300 p-3 text-amber-800">Discount</td>
                              <td className="border border-gray-300 p-3 text-right text-amber-800 font-medium">- ₹{Number(pricing.breakdown.discount).toLocaleString('en-IN')}</td>
                            </tr>
                          ) : null}
                          <tr className="bg-green-50">
                            <td className="border border-gray-300 p-3 font-bold text-lg text-green-900">Total Amount</td>
                            <td className="border border-gray-300 p-3 text-right font-bold text-lg text-green-900">₹{Number(pricing.breakdown.total_amount || pricing.total || 0).toLocaleString('en-IN')}</td>
                          </tr>
                        </>
                      ) : (
                        <>
                          {/* Custom breakdown for Outstation/Other services - only show rows that have values */}
                          {Number(pricing.breakdown.total_running) > 0 ? (
                            <tr>
                              <td className="border border-gray-300 p-3">Total Running</td>
                              <td className="border border-gray-300 p-3 text-right font-medium">₹{Number(pricing.breakdown.total_running).toLocaleString('en-IN')}</td>
                            </tr>
                          ) : null}
                          {Number(pricing.breakdown.bata) > 0 ? (
                            <tr>
                              <td className="border border-gray-300 p-3">Bata (Driver Allowance)</td>
                              <td className="border border-gray-300 p-3 text-right font-medium">₹{Number(pricing.breakdown.bata).toLocaleString('en-IN')}</td>
                            </tr>
                          ) : null}
                          {Number(pricing.breakdown.toll) > 0 ? (
                            <tr>
                              <td className="border border-gray-300 p-3">Toll Charges</td>
                              <td className="border border-gray-300 p-3 text-right font-medium">₹{Number(pricing.breakdown.toll).toLocaleString('en-IN')}</td>
                            </tr>
                          ) : null}
                          {Number(pricing.breakdown.permit) > 0 ? (
                            <tr>
                              <td className="border border-gray-300 p-3">Permit Charges</td>
                              <td className="border border-gray-300 p-3 text-right font-medium">₹{Number(pricing.breakdown.permit).toLocaleString('en-IN')}</td>
                            </tr>
                          ) : null}
                          {Number(pricing.total) > 0 ? (
                            <tr className="bg-blue-50 font-semibold">
                              <td className="border border-gray-300 p-3">Subtotal</td>
                              <td className="border border-gray-300 p-3 text-right">₹{Number(pricing.total).toLocaleString('en-IN')}</td>
                            </tr>
                          ) : null}
                          {Number(pricing.breakdown.advance_fuel) > 0 ? (
                            <tr className="bg-red-50">
                              <td className="border border-gray-300 p-3 text-red-700">Less: Advance - Fuel</td>
                              <td className="border border-gray-300 p-3 text-right text-red-700 font-medium">- ₹{Number(pricing.breakdown.advance_fuel).toLocaleString('en-IN')}</td>
                            </tr>
                          ) : null}
                          {Number(pricing.breakdown.advance_achal) > 0 ? (
                            <tr className="bg-red-50">
                              <td className="border border-gray-300 p-3 text-red-700">Less: Advance - Achal</td>
                              <td className="border border-gray-300 p-3 text-right text-red-700 font-medium">- ₹{Number(pricing.breakdown.advance_achal).toLocaleString('en-IN')}</td>
                            </tr>
                          ) : null}
                          {Number(pricing.breakdown.discount) > 0 ? (
                            <tr className="bg-amber-50">
                              <td className="border border-gray-300 p-3 text-amber-800">Discount</td>
                              <td className="border border-gray-300 p-3 text-right text-amber-800 font-medium">- ₹{Number(pricing.breakdown.discount).toLocaleString('en-IN')}</td>
                            </tr>
                          ) : null}
                          <tr className="bg-green-50">
                            <td className="border border-gray-300 p-3 font-bold text-lg text-green-900">Amount Due</td>
                            <td className="border border-gray-300 p-3 text-right font-bold text-lg text-green-900">₹{Number(pricing.breakdown.due_amount ?? pricing.total ?? 0).toLocaleString('en-IN')}</td>
                          </tr>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Standard pricing */}
                      <tr>
                        <td className="border border-gray-300 p-3">{pricing.details}</td>
                        <td className="border border-gray-300 p-3 text-right font-medium">
                          {pricing.baseAmount > 0 ? `₹${pricing.baseAmount}` : 'As per trip'}
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="border border-gray-300 p-3 font-bold text-lg">Total Amount</td>
                        <td className="border border-gray-300 p-3 text-right font-bold text-lg text-gray-900">
                          {pricing.baseAmount > 0 ? `₹${pricing.baseAmount}` : 'As per agreement'}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
              {!pricing.isCustom && (
                <p className="text-sm text-gray-600 mt-3 italic">
                  * Excludes: Toll, Parking, Driver allowance, and Permit charges (if applicable)
                </p>
              )}
            </div>

            {/* Thank You Message */}
            <div className="mt-10 pt-6 border-t-2 border-gray-300 text-center">
              <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-r-md p-5 text-left">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Thank you for choosing SG Cabs.</h3>
                <p className="text-sm text-gray-700">
                  We appreciate your trust and look forward to serving you on your next journey.
                </p>
              </div>

              {/* Feedback Section */}
              <div className="mt-5 bg-gray-50 border border-gray-200 rounded-md p-5 text-left">
                <h4 className="text-base font-semibold text-gray-900 mb-1">How was your experience?</h4>
                <p className="text-sm text-gray-700 mb-3">
                  Your feedback helps us serve you better. Please share your experience at the link below.
                </p>
                <a
                  href="https://sg-cabs.com/feedback"
                  className="inline-block bg-gray-900 hover:bg-black text-white font-medium px-5 py-2 rounded-md transition-colors text-sm"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  sg-cabs.com/feedback
                </a>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Terms & Conditions</h3>
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-300">
                <div className="space-y-4 text-sm text-gray-700">
                  <p className="leading-relaxed">
                    This document has been issued upon the passenger's request. <strong>SG Cabs</strong> operates as a facilitation service and does not collect any fees or commissions from passengers under this segment. Accordingly, no tax invoice is issued to passengers for these services.
                  </p>
                  
                  <p className="leading-relaxed">
                    This document is valid and may be used for official, billing, or reimbursement purposes.
                  </p>
                  
                  <div className="pt-3 border-t border-gray-300">
                    <p className="leading-relaxed italic">
                      <strong>*</strong> "Selected Price" refers to the mutually agreed fare between the user and the driver, based on the estimated price range at the time of booking.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 text-center text-gray-600 border-t border-gray-200 pt-4 text-sm">
              <p className="font-semibold text-gray-900">SG Cabs — Your Journey, Our Priority</p>
              <p className="mt-2">+91 75078 67648 &nbsp;|&nbsp; sgcabs.official@gmail.com</p>
              <p className="mt-1">sg-cabs.com &nbsp;|&nbsp; Bangalore, Karnataka</p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
            overflow: visible !important;
          }

          /* Remove the rest of the app from the print flow so it cannot create blank pages */
          body > *:not(#invoice-print-area-overlay):not(style):not(script) {
            display: none !important;
          }

          /* Hide everything on the page by default */
          body * {
            visibility: hidden !important;
          }

          /* Show only the invoice print area and its descendants */
          #invoice-print-area,
          #invoice-print-area * {
            visibility: visible !important;
          }

          /* Reset ancestors so they don't form a positioning/clipping context */
          #invoice-print-area-overlay,
          #invoice-print-area-wrapper {
            position: static !important;
            inset: auto !important;
            top: auto !important;
            left: auto !important;
            right: auto !important;
            bottom: auto !important;
            width: auto !important;
            max-width: none !important;
            height: auto !important;
            max-height: none !important;
            margin: 0 !important;
            padding: 0 !important;
            background: transparent !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            overflow: visible !important;
            display: block !important;
          }

          /* Position the invoice itself at the document origin so hidden siblings don't push it down */
          #invoice-print-area {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            box-shadow: none !important;
            overflow: visible !important;
            display: block !important;
          }

          .print\\:hidden {
            display: none !important;
          }

          img {
            display: block !important;
          }

          table, tr, td, th {
            page-break-inside: avoid !important;
          }

          @page {
            margin: 1cm;
            size: A4;
          }
        }
      `}</style>
    </>,
    document.body
  );
};
